import * as React from "react";
import { getSnapshotGridColumnWidth, type SnapshotGridColumnWidths } from "@/modules/snapshots/grid/table/layout";
import { safeSetLocalStorageItem } from "@/modules/snapshots/grid/localStorage";
import { MAX_STORED_COLUMN_WIDTHS } from "@/modules/snapshots/grid/constants";

export const readSnapshotGridColumnWidths = (storageKey: string): SnapshotGridColumnWidths => {
  try {
    if (typeof localStorage === "undefined") return {};
    const stored = localStorage.getItem(storageKey);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};

    return Object.entries(parsed)
      .slice(0, MAX_STORED_COLUMN_WIDTHS)
      .reduce<SnapshotGridColumnWidths>((widths, [columnId, width]) => {
        if (typeof width === "number" && Number.isFinite(width)) {
          widths[columnId] = getSnapshotGridColumnWidth(columnId, { [columnId]: width });
        }
        return widths;
      }, {});
  } catch {
    return {};
  }
};

/** Column widths persisted per collection; re-read whenever the collection (storage key) changes. */
export const useSnapshotGridColumnWidths = (collectionId: string | undefined) => {
  const columnWidthsKey = `snapshot-column-widths-${collectionId}`;
  const [columnWidths, setColumnWidths] = React.useState<SnapshotGridColumnWidths>(() =>
    readSnapshotGridColumnWidths(columnWidthsKey),
  );

  React.useEffect(() => {
    setColumnWidths(readSnapshotGridColumnWidths(columnWidthsKey));
  }, [columnWidthsKey]);

  const handleColumnResize = React.useCallback(
    (columnId: string, width: number) => {
      setColumnWidths(prev => {
        const nextWidth = getSnapshotGridColumnWidth(columnId, { [columnId]: width });
        if (prev[columnId] === nextWidth) return prev;

        const next = { ...prev, [columnId]: nextWidth };
        safeSetLocalStorageItem(columnWidthsKey, JSON.stringify(next));
        return next;
      });
    },
    [columnWidthsKey],
  );

  return { columnWidths, handleColumnResize };
};
